import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const QUESTIONS_PER_PAGE = 10;

const QuestionList = ({ questions }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  if (!questions || questions.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">No questions posted yet.</p>
        </CardContent>
      </Card>
    );
  }

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);

  const handlePageChange = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  const handleQuestionClick = (id) => {
    navigate(`/dashboard/questions/${id}`);
  };

  const paginatedQuestions = questions.slice(
    (currentPage - 1) * QUESTIONS_PER_PAGE,
    currentPage * QUESTIONS_PER_PAGE
  );

  return (
    <div className="space-y-4">
      {paginatedQuestions.map((q) => {
        const formattedTags = Array.isArray(q.tags)
          ? q.tags.map(tag => (typeof tag === 'object' && tag.name ? tag.name : tag)).join(', ')
          : typeof q.tags === 'string'
            ? q.tags.split(',').join(', ')
            : 'No tags';

        const detailsContent =
          q.details?.length > 150
            ? q.details.slice(0, 150) + '...'
            : q.details || '<em>No details provided</em>';

        return (
          <Card 
            key={q.id} 
            className="cursor-pointer hover:bg-accent/50 transition-colors"
            onClick={() => handleQuestionClick(q.id)}
          >
            <CardHeader>
              <CardTitle className="text-xl">{q.title || 'Untitled Question'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
            <div
                className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: detailsContent }}
            />

              <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                <div>
              <strong>Tags: </strong>
              <span>{formattedTags}</span>
            </div>
                <Separator orientation="vertical" className="h-4" />
                <div>
              <strong>Posted by: </strong>
              <span>{q.user?.email ? q.user.email.split('@')[0] : 'Unknown user'}</span>
            </div>
                <Separator orientation="vertical" className="h-4" />
                <div className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4" />
                  <span>{q.replies?.length || 0} replies</span>
                </div>
          </div>
            </CardContent>
          </Card>
        );
      })}

      <div className="flex justify-center gap-2 mt-6">
        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </Button>

        {[...Array(totalPages)].map((_, index) => (
          <Button
            key={index + 1}
            variant={currentPage === index + 1 ? "default" : "outline"}
            onClick={() => handlePageChange(index + 1)}
          >
            {index + 1}
          </Button>
        ))}

        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default QuestionList;
